// 音频工具类，用于播放告警声音和语音提示
export default {
  // 存储持续播放的音频上下文和振荡器
  continuousAlarms: new Map(),
  
  // 初始化音频系统，处理浏览器自动播放限制
  initAudioSystem() {
    try {
      // 检查浏览器是否支持语音合成
      if ('speechSynthesis' in window) {
        console.log('语音合成功能可用');
        
        // 尝试获取可用的语音列表
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          console.log('找到可用语音:', voices.map(v => v.name).join(', '));
        } else {
          // 语音列表可能需要时间加载
          window.speechSynthesis.onvoiceschanged = function() {
            const loadedVoices = window.speechSynthesis.getVoices();
            console.log('语音列表加载完成:', loadedVoices.map(v => v.name).join(', '));
          };
        }
      } else {
        console.warn('浏览器不支持语音合成');
      }
      
      // 尝试创建音频上下文，激活音频系统
      if (window.AudioContext || window.webkitAudioContext) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        // 播放一个静音音频，激活音频系统
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        gainNode.gain.value = 0; // 静音
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
        console.log('音频系统已激活');
      }
    } catch (error) {
      console.error('初始化音频系统失败:', error);
    }
  },
  
  // 播放告警声音
  playAlarmSound() {
    try {
      // 创建音频上下文
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      // 创建振荡器
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // 连接节点
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 设置参数
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.5);
      
      // 设置音量
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      // 播放
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('播放告警声音失败:', error);
    }
  },
  
  // 播放警告声音
  playWarningSound() {
    try {
      // 创建音频上下文
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      // 创建振荡器
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // 连接节点
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 设置参数
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
      
      // 设置音量
      gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      // 播放
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error('播放警告声音失败:', error);
    }
  },
  
  // 开始持续播放告警声音
  startContinuousAlarmSound(key) {
    try {
      // 停止之前的持续告警
      this.stopContinuousAlarmSound(key);
      
      // 创建音频上下文
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      // 创建振荡器
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // 连接节点
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 设置参数
      oscillator.type = 'sine';
      
      // 设置音量
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      
      // 创建循环的频率变化
      let currentTime = audioContext.currentTime;
      const duration = 1; // 每个循环的持续时间
      
      // 循环播放
      for (let i = 0; i < 100; i++) { // 播放100次循环，大约100秒
        oscillator.frequency.setValueAtTime(800, currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(400, currentTime + duration / 2);
        oscillator.frequency.setValueAtTime(400, currentTime + duration / 2);
        oscillator.frequency.exponentialRampToValueAtTime(800, currentTime + duration);
        
        gainNode.gain.setValueAtTime(0.1, currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.05, currentTime + duration / 2);
        gainNode.gain.exponentialRampToValueAtTime(0.1, currentTime + duration);
        
        currentTime += duration;
      }
      
      // 播放
      oscillator.start();
      
      // 存储音频上下文和振荡器，以便后续停止
      this.continuousAlarms.set(key, { audioContext, oscillator });
      console.log('开始持续播放告警声音:', key);
    } catch (error) {
      console.error('开始持续播放告警声音失败:', error);
    }
  },
  
  // 停止持续播放告警声音
  stopContinuousAlarmSound(key) {
    try {
      const alarm = this.continuousAlarms.get(key);
      if (alarm) {
        const { audioContext, oscillator } = alarm;
        oscillator.stop();
        audioContext.close();
        this.continuousAlarms.delete(key);
        console.log('停止持续播放告警声音:', key);
      }
    } catch (error) {
      console.error('停止持续播放告警声音失败:', error);
    }
  },
  
  // 播放语音提示，朗读具体点位和报警类型
  playVoiceNotification(deviceName, message, status) {
    try {
      // 检查浏览器是否支持语音合成
      if ('speechSynthesis' in window) {
        // 构建语音提示文本
        const statusText = status === 'alarm' ? '告警' : '警告';
        const notificationText = `${statusText}，${deviceName}，${message}`;
        
        console.log('准备播放语音提示:', notificationText);
        
        // 确保语音合成处于就绪状态
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
        
        // 清除之前的语音队列
        window.speechSynthesis.cancel();
        
        // 创建语音合成实例
        const speech = new SpeechSynthesisUtterance(notificationText);
        
        // 设置语音参数
        speech.lang = 'zh-CN';
        speech.volume = 1;
        speech.rate = 1;
        speech.pitch = 1;
        
        // 尝试设置中文语音
        const voices = window.speechSynthesis.getVoices();
        const chineseVoice = voices.find(voice => 
          voice.lang === 'zh-CN' || voice.name.includes('Chinese') || voice.name.includes('中文')
        );
        if (chineseVoice) {
          speech.voice = chineseVoice;
          console.log('使用中文语音:', chineseVoice.name);
        } else {
          console.log('未找到中文语音，使用默认语音');
        }
        
        // 添加事件监听器以便调试
        speech.onstart = function() {
          console.log('语音提示开始播放');
        };
        
        speech.onend = function() {
          console.log('语音提示播放结束');
        };
        
        speech.onerror = function(event) {
          console.error('语音提示播放错误:', event.error);
        };
        
        speech.onpause = function() {
          console.log('语音提示暂停');
        };
        
        speech.onresume = function() {
          console.log('语音提示恢复');
        };
        
        // 播放语音
        window.speechSynthesis.speak(speech);
        console.log('语音提示已添加到播放队列');
      } else {
        console.warn('浏览器不支持语音合成');
      }
    } catch (error) {
      console.error('播放语音提示失败:', error);
    }
  }
};